import React, { useEffect, useMemo, useState } from 'react';
import Placeholder from './Placeholder';
import ProductSelector from './ProductSelector';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { useDispatch } from 'react-redux';
import { setActiveProduct } from '../../Reducers/ProductPage/Actions/setActiveProduct.action';
import { ProductPageModel } from '../../Types/ProductPageModel';
import { translate } from '../../Services/translation';
import ProductPageTabsContainer from '../ProductPageTabs/ProductPageTabsContainer';
import ProductPageImageGallery from './ProductPageImageGallery';
import ProductPageMobileImages from './ProductPageMobileImages';
import ProductPageBuySection from './ProductPageBuySection';
import ProductPageProductInformation from './ProductPageProductInformation';
import ProductPageUsps from './ProductPageUsps';
import ProductRelationBlockContainer from './ProductRelationBlockContainer';
import StockStatus from '../StockStatus/StockStatus';
import ProductPageBuyGuideContainer from './ProductPageBuyGuideContainer';
import ProductPageLinkBlock from './ProductPageLinkBlock';
import ProductPageStructuredData from './ProductPageStructuredData';
import YotpoUgc from '../Yotpo/YotpoUgc';
import useYotpoBottomLine from '../../Hooks/useYotpoBottomLine';
import ProductPageWallpaperCalculatorModalWrapper from './ProductPageWallpaperCalculatorModalWrapper';
import InformationModal from './ProductPageInformationModal';
import DetailedListBuyForm from '../ProductCards/DetailedProductCardComponents/DetailedListBuyForm';
import clsx from 'clsx';
import useIsStoreScreen from '../../Hooks/useIsStoreScreen';
import { trackingServiceProductPageViewEvent } from '../../Services/Tracking.service';
import { useTypedDispatch } from '../../Hooks/useTypedDispatch';
import { StockStatusLevel } from '../../constants';
import BuySampleButton from '../BuyButton/BuySampleButton';
import DetailedListBuyFormPDP from '../ProductCards/DetailedProductCardComponents/DetailedListBuyFormPDP';
import ProductPageRoomvoButton from './ProductPageRoomvoButton';
import DiscontinuedItem from '../DiscontinuedItem/DiscontinuedItem';

const ProductPage = (model: ProductPageModel) => {
  const dispatch = useTypedDispatch();
  const { currentProduct: productFromState, loadingProductId } =
    useTypedSelector((state) => state.productPage);
  const { currency } = useTypedSelector((state) => state.cart);
  const isB2BUser = useTypedSelector(
    (state) => state.userInfo.current?.isBusinessCustomer
  );
  const isStoreScreen = useIsStoreScreen();
  const currentProduct = useMemo(
    () => productFromState ?? model,
    [productFromState, model]
  );
  const yotpoBottomLine = useYotpoBottomLine(
    currentProduct.productItem.id
  );
  const renameBreadcrumb = () => {
    const breadcrumb = document.querySelectorAll('.breadcrumb-element');
    const lastBreadcrumb = breadcrumb[breadcrumb.length - 1];
    if (lastBreadcrumb) {
      lastBreadcrumb.innerHTML = currentProduct.productItem.name;
    }
  };

  useEffect(() => {
    renameBreadcrumb();
  }, [currentProduct.productItem.id]);
  const [quantity, setQuantity] = useState<number | undefined>(0);
  const [boxesAmount, setBoxesAmount] = useState<number>(0);
  const [rollsAmount, setRollsAmount] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [packageSize, setPackageSize] = useState<number>(0);
  useMemo(
    () =>
      setPackageSize(
        currentProduct.productItem.itemPackageSize
          ? currentProduct.productItem.itemPackageSize
          : 1
      ),
    []
  );

  return (
    <div itemScope itemType="http://schema.org/Product">
      <ProductPageMobileImages
        images={currentProduct.productItem.images}
        videoUrl={currentProduct.productItem.videoUrl}
      />
      <div className="container mb-8 grid grid-cols-1 gap-4 store-screen:grid-cols-8 lg:grid-cols-3">
        <div className="col-span-2 hidden gap-4 store-screen:col-span-5 lg:grid">
          <ProductPageImageGallery
            images={currentProduct.productItem.images}
            videoUrl={currentProduct.productItem.videoUrl}
          />
          {currentProduct.productItem.id && (
            <div className="lg:block">
              <YotpoUgc productId={currentProduct.productItem.id} />
            </div>
          )}
        </div>
        <div className="store-screen:col-span-3 lg:-mt-8 lg:flex lg:justify-center lg:px-4 xl:px-8">
          <div className="w-full self-start pt-6 lg:sticky lg:top-0 lg:max-w-[568px] lg:pt-8">
            <ProductPageProductInformation
              title={currentProduct.productItem.name}
              priceModel={currentProduct.productItem.price}
              bottomLine={yotpoBottomLine}
              product={currentProduct.productItem}
              isB2b={isB2BUser}
              articleNumber={currentProduct.productItem.id}
            />
            {currentProduct.productItem.informationModal?.active && (
              <InformationModal
                {...currentProduct.productItem.informationModal}
              />
            )}
            {currentProduct.stockStatus && (
              <StockStatus {...currentProduct.stockStatus} />
            )}
            {currentProduct.productItem.itemStatus && (
              <DiscontinuedItem
                itemStatus={currentProduct.productItem.itemStatus}
              />
            )}
            {isB2BUser ? (
              <>
                <div className={clsx('mb-3 grid grid-cols-4 gap-2')}>
                  <DetailedListBuyFormPDP
                    product={currentProduct.productItem}
                    quantity={quantity ?? 1}
                    boxesAmount={boxesAmount}
                    rollsAmount={rollsAmount}
                    totalAmount={totalAmount}
                    packageSize={packageSize}
                    setQuantity={setQuantity}
                    setBoxesAmount={setBoxesAmount}
                    setRollsAmount={setRollsAmount}
                    setTotalAmount={setTotalAmount}
                    isProductPage={true}
                  />
                </div>
                {currentProduct.productItem.stockStatus?.level !==
                  StockStatusLevel.Inactive &&
                  !currentProduct.disableBuyOption &&
                  currentProduct.productItem.samples?.map((sample) => (
                    <BuySampleButton
                      {...sample}
                      className="col-span-4 mb-3 border border-solid border-gray-700"
                      key={`sample-${sample.articleNumber}`}
                    />
                  ))}
              </>
            ) : (
              <ProductPageBuySection product={currentProduct} />
            )}
            {(currentProduct.buyGuideBlocks != null ||
              (!model.hideRoomvoButton &&
                currentProduct.productItem.simulatorCompatible)) && (
              <div
                className="roomvo-container flex items-center justify-between"
                data-product-type-str="wall"
              >
                {model.buyGuideBlocks != null && (
                  <ProductPageBuyGuideContainer />
                )}
                {!model.hideRoomvoButton &&
                  currentProduct.productItem.simulatorCompatible && (
                    <ProductPageRoomvoButton
                      id={currentProduct.productItem.id}
                    />
                  )}
              </div>
            )}
            <ProductPageUsps uspBlock={model.uspBlock} />
            {currentProduct.variants?.length > 1 && (
              <ProductSelector
                items={currentProduct.variants}
                onVariantSelected={(id) =>
                  dispatch(
                    setActiveProduct({
                      id,
                      currentProductId: currentProduct.productItem.id,
                    })
                  )
                }
                loadingProductId={loadingProductId}
                selectedProductId={currentProduct.productItem.id}
              />
            )}
          </div>
        </div>
      </div>
      <ProductPageTabsContainer productPage={currentProduct} />
      {currentProduct.productItem.id && (
        <div className="container lg:hidden">
          <YotpoUgc productId={currentProduct.productItem.id} />
        </div>
      )}
      <div className="container mb-16 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {currentProduct.productItem.id &&
          model.allowWallpaperCalculator && (
            <ProductPageWallpaperCalculatorModalWrapper
              articleNumber={currentProduct.productItem.id}
              rollWidth={currentProduct.widthMeters}
              rollLength={currentProduct.lengthMeters}
              patternSizeMeters={currentProduct.patternSizeMeters}
              fitment={currentProduct.fitment}
              hasPrice={!!currentProduct.productItem.price?.price}
            />
          )}
        {model.arApplicationUrl && (
          <ProductPageLinkBlock
            className="lg:hidden"
            url={model.arApplicationUrl}
            preTitle={translate('product-page.ar-application')}
            title={translate('product-page.ar-application.title')}
            description={translate(
              'product-page.ar-application.description'
            )}
            buttonText={translate('product-page.ar-application.button')}
            iconName="cube-focus"
            openInNewWindow={true}
          />
        )}
        {!model.hideRoomvoButton &&
          currentProduct.productItem.simulatorCompatible && (
            <ProductPageLinkBlock
              preTitle={translate('product-page.wallpaper-simulator')}
              title={translate('product-page.wallpaper-simulator.title')}
              description={translate(
                'product-page.wallpaper-simulator.description'
              )}
              buttonText={translate(
                'product-page.wallpaper-simulator.button'
              )}
              iconName="cube-focus"
              displayArButton={true}
              displayButton={false}
              productId={currentProduct.productItem.id}
            />
          )}
      </div>
      <div className="container mb-16">
        <ProductRelationBlockContainer
          productRelations={[
            currentProduct.accessoriesProducts,
            currentProduct.similarProducts,
            currentProduct.matchingProducts,
          ]}
        />
      </div>
      <ProductPageStructuredData productPage={currentProduct} />
    </div>
  );
};

export default ProductPage;
